/**
 * Point d'entrée unique du formatage financier, partagé par le front public et
 * l'admin. Le formatage dépend de la locale : il vit donc avec l'i18n.
 *
 * Deux unités coexistent dans le modèle de données :
 * - les montants Stripe (Order.amount, soldes, factures) sont en **centimes** ;
 * - les prix produits (Product.price) sont dans l'**unité principale**.
 *
 * Chaque unité a sa fonction dédiée : le nom porte l'information, un import
 * erroné ne peut plus produire un montant faux d'un facteur 100.
 */
/** Repli si la devise est absente ou invalide (donnée héritée). */
const FALLBACK_CURRENCY = "EUR";
// Un code ISO 4217 est fait de trois lettres. Intl lève une RangeError sinon,
// ce qui ferait planter le rendu pour une simple donnée mal saisie.
function normalizeCurrency(currency) {
    if (!currency) {
        return FALLBACK_CURRENCY;
    }
    const normalized = currency.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(normalized) ? normalized : FALLBACK_CURRENCY;
}
/**
 * Cœur du formatage : montant dans l'unité principale, devise, locale.
 * Fonction pure — utilisable hors rendu React.
 */
export function formatMoney(amount, currency, locale) {
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: normalizeCurrency(currency),
    }).format(safeAmount);
}
/** Montant stocké en centimes (Stripe, Order.amount, soldes, factures). */
export function formatCents(cents, currency, locale) {
    const safeCents = Number.isFinite(cents) ? cents : 0;
    return formatMoney(safeCents / 100, currency, locale);
}
/** Montant stocké dans l'unité principale (Product.price). */
export function formatUnits(units, currency, locale) {
    return formatMoney(units, currency, locale);
}
/**
 * Valeur brute pour un `<input type="number">`, à partir de centimes.
 *
 * Volontairement **non localisée** : le champ n'accepte que le point comme
 * séparateur décimal et sa valeur est relue par `parseFloat`. Y injecter un
 * montant formaté (« 1 234,50 € ») invaliderait l'attribut et induirait
 * l'utilisateur à saisir une valeur que le champ refuse.
 */
export function toAmountInputValue(cents) {
    const safeCents = Number.isFinite(cents) ? cents : 0;
    return (safeCents / 100).toFixed(2);
}
//# sourceMappingURL=money.js.map